import React, { CSSProperties, useRef } from 'react';
import { vizSilver } from '@ardoq/design-tokens';
import styled, { css } from 'styled-components';
import { TimelinePhase } from '../../types';
import { TIMELINE_INFINITE_PHASE_INDICATOR_WIDTH } from '../../items/consts';
import { TimelinesGridPrecisionUnit } from '@ardoq/api-types';
import { getTextureAsCSS } from '@ardoq/icons';
import { POPOVER_ELEMENT_TYPE } from '../../consts';

type TimelineChartPhaseArgs = {
  model: TimelinePhase;
  className?: string;
  infiniteLeft: boolean;
  infiniteRight: boolean;
  dynamicPhaseStyles: CSSProperties;
  modelId: string;
  index: number;
  gridPrecision: TimelinesGridPrecisionUnit;
  graphNodeId: string;
  phasesRowIndex: number;
};

const phaseBaseCss = css`
  position: absolute;
  z-index: 3;
`;

const PhaseContainer = styled.div<{ $textureIndex: number }>`
  ${phaseBaseCss}
  background-color: ${vizSilver};
  margin: 0;
  overflow: hidden;
  border: 1px solid black;
  ${({ $textureIndex }) => getTextureAsCSS($textureIndex)}
`;

const InfinitePhaseIndicator = styled.div`
  ${phaseBaseCss}
  width: ${TIMELINE_INFINITE_PHASE_INDICATOR_WIDTH}px;
  background-image: repeating-linear-gradient(
    90deg,
    #000,
    #000 1px,
    transparent 1px,
    transparent 2px
  );
`;

export const Phase = React.memo(
  ({
    dynamicPhaseStyles,
    model,
    className,
    infiniteLeft,
    infiniteRight,
    graphNodeId,
    phasesRowIndex,
  }: TimelineChartPhaseArgs) => {
    const phaseContainerRef = useRef<HTMLDivElement | null>(null);

    const { left, height, top, width = 0 } = dynamicPhaseStyles;

    const widthNumber = Number(width);
    const leftNumber = Number(left);

    return (
      <>
        {infiniteLeft ? (
          <InfinitePhaseIndicator
            style={{
              left: leftNumber - TIMELINE_INFINITE_PHASE_INDICATOR_WIDTH,
              height,
              top,
            }}
          />
        ) : null}
        <PhaseContainer
          ref={phaseContainerRef}
          className={className}
          style={dynamicPhaseStyles}
          data-texture-index={model.fieldIndex}
          data-graph-node-id={graphNodeId}
          data-phases-row-index={phasesRowIndex}
          $textureIndex={model.fieldIndex}
          data-popover-element-type={POPOVER_ELEMENT_TYPE.PHASE}
        />
        {infiniteRight ? (
          <InfinitePhaseIndicator
            style={{
              left: leftNumber + widthNumber,
              height,
              top,
            }}
          />
        ) : null}
      </>
    );
  }
);
